import {
  TrackingElementContext,
  TrackingPageTypeWithoutId,
  TrackingPromotion,
  TrackingPromotionCreative,
  TrackingPromotionDestination,
  TrackingSectionContext,
} from '../../../types';
import {
  TrackingClientState,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {
  addContainerToHistory,
  getSourceHistoryEventData,
} from '../helpers/sourceHistory';
import {stringifyEventData} from '../helpers/stringifyEventData';

interface PromotionDataGA4 {
  promotion_id: string | number;
  promotion_name: string;

  creative_name: TrackingPromotionCreative | string;
  creative_slot: string;
  index: number;
  destination: string;
  destination_url: string | null;
  linktarget_id: string;
  linktarget: string | null;
  source: string | null;
  location_id: string | null;
  position_bi: string;
  is_sponsored: boolean;
}

type PromotionData = {
  GA4: PromotionDataGA4;
};

export interface PromotionImpressionEvent {
  GA4: {
    event: 'view_promotion';
    content_name: string;
    page_type: string;
    page_type_id: string;
    ecommerce: {
      items: PromotionData['GA4'][];
    };
  };
}

export interface PromotionClickEvent {
  GA4: {
    event: 'select_promotion';
    content_name: string;
    page_type: string;
    page_type_id: string;
    ecommerce: {
      items: PromotionData['GA4'][];
    };
  };
}

export type PromotionEvent = {
  GA4: PromotionImpressionEvent['GA4'] | PromotionClickEvent['GA4'];
};

const dataLayerDestinationDescription = {
  'no-destination': 'no_link',
  external: 'external_link',
  video: 'video',
  liveshopping: 'liveshopping',
};

const getDataLayerDestinationUrl = ({
  destination,
}: {
  destination: TrackingPromotionDestination;
}): string | null => {
  switch (destination.type) {
    case 'internal':
      return destination.page.path;
    case 'from-endpoint':
    case 'liveshopping':
    case 'external':
      return destination.url;
    case 'video':
      return 'video';
    case 'no-destination':
      return null;
  }
};

export const trackPromotionInteraction =
  ({
    type,
    section,
    element,
    promotion,
    pageType: pageTypeOverride,
  }: {
    type: 'impression' | 'click';
    section: TrackingSectionContext;
    element: TrackingElementContext;
    promotion: TrackingPromotion;
    pageType?: string;
  }) =>
  (clientState: TrackingClientStateHolder): PromotionEvent => {
    const currentState = clientState.getCurrent();
    const {currentPage} = currentState;

    if (type === 'click') {
      addContainerToHistory({section, clientState});
    }

    if (promotion.originClickOverride) {
      clientState.update({originClick: promotion.originClickOverride});
    }

    const {promotionsGA4} = getPromotions({
      currentState,
      section,
      element,
      promotion,
    });

    let GA4Promotion:
      | PromotionImpressionEvent['GA4']
      | PromotionClickEvent['GA4'];

    const pageTypeId = (() => {
      if (promotion.creative === TrackingPromotionCreative.Highlight) {
        if (promotion.trackHighlightIdAsPageId) {
          return `${promotion.highlightId}`;
        }
      }

      if (
        promotion.creative === TrackingPromotionCreative.Brand &&
        pageTypeOverride !== TrackingPageTypeWithoutId.Browse
      ) {
        return `${promotion.id}`;
      }

      return currentPage?.type === 'detailed'
        ? `${currentPage.page?.id || ''}`
        : '';
    })();

    // we should prefer the pageType from the promotion if it is not 'other',
    // otherwise we should use the pageType from the currentPage
    const pageType = (() => {
      if (
        pageTypeOverride &&
        pageTypeOverride !== TrackingPageTypeWithoutId.Other
      ) {
        return pageTypeOverride;
      }

      return currentPage?.page?.type ?? TrackingPageTypeWithoutId.Other;
    })();

    if (type === 'impression') {
      GA4Promotion = {
        event: 'view_promotion',
        content_name: currentPage?.page?.path || '',
        page_type: pageType,
        page_type_id: pageTypeId,
        ecommerce: {
          items: promotionsGA4,
        },
      } as PromotionImpressionEvent['GA4'];
    } else {
      GA4Promotion = {
        event: 'select_promotion',
        content_name: currentPage?.page?.path || '',
        page_type: pageType,
        page_type_id: pageTypeId,
        ecommerce: {
          items: promotionsGA4,
        },
      } as PromotionClickEvent['GA4'];
    }
    return {
      GA4: GA4Promotion,
    };
  };

const getPromotions = ({
  currentState,
  section,
  element,
  promotion,
}: {
  currentState: TrackingClientState;
  section: TrackingSectionContext;
  element: TrackingElementContext;
  promotion: TrackingPromotion;
}) => {
  const sourceHistory = getSourceHistoryEventData({
    state: currentState,
    currentSourceSection: section,
  });

  const destination = (() => {
    if (promotion.destination.type === 'from-endpoint') {
      return promotion.destination.destination;
    }

    return stringifyEventData([
      promotion.destination.type === 'internal'
        ? promotion.destination.page.type
        : dataLayerDestinationDescription[promotion.destination.type],
      promotion.destination.type === 'internal'
        ? promotion.destination.page.id
        : '',
    ]);
  })();

  const destinationUrl = getDataLayerDestinationUrl({
    destination: promotion.destination,
  });

  let placement = sourceHistory.source?.split('|')[3] ?? '';
  if (promotion.locationId) {
    placement = promotion.locationId;
  }

  let updatedSource = sourceHistory.source;
  if (
    promotion.creative === TrackingPromotionCreative.Highlight &&
    sourceHistory.source
  ) {
    const splitSource = sourceHistory.source.split('|');
    splitSource[0] = promotion.creative;
    splitSource[1] = promotion.highlightId?.toString() || '';
    updatedSource = splitSource.join('|');
  }

  const promotionsGA4: PromotionDataGA4[] = [
    {
      promotion_id: promotion.id,
      promotion_name: promotion.name,
      creative_name: promotion.creativeName || promotion.creative,
      creative_slot:
        promotion.creativeSlot !== undefined
          ? promotion.creativeSlot
          : promotion.creative,
      index: element.elementIndexInSection + 1,
      destination,
      destination_url: destinationUrl,
      linktarget_id: destination,
      linktarget: destinationUrl,
      source: updatedSource,
      location_id: placement,
      position_bi: stringifyEventData([
        section.sectionIndexOnPage + 1 || 0,
        element.rowIndexInSection + 1 || 0,
        element.columnIndexInSection + 1 || 0,
        section.type === 'reco' ? section.initiallyVisibleColumns : 1,
      ]),
      is_sponsored: Boolean(promotion.isSponsored),
    },
  ];

  return {promotionsGA4};
};
